import { useState } from 'react';

import { getAspectRatioForHeroBannerImages } from '@/utils/aspectRatioUtils';

import { MediaWrapper } from '@/cms/MediaWrapper';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { Loader } from '@/components/Loader';
import type { ContentfulImageWithFocalPointFragment } from '@/graphql/contentful/api';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { generateDataTestId } from '@/lib/tests';

import type { CardProps } from '../../types';
import { CardContent } from '../CardContent';

import * as GS from '../../Card.styles';
import * as S from './HeroBanner.styles';

export const HeroBanner = (props: CardProps) => {
  const {
    aspectRatio,
    backgroundAlignment,
    backgroundColor,
    className,
    contentAlignment,
    countdown,
    isHeroCard,
    lazyLoadVideo,
    loaderProps,
    media,
    mediaAnimationEffect,
    mediaWrapperType,
    preload,
    sizes,
    textAlignment,
    type,
    useLoader,
    visibleInDesktop,
    visibleInMobile,
  } = props;

  const { deviceType } = useDeviceDetect();
  const hasBackgroundImage = !!media?.video?.url || !!media?.image?.url;

  const aspectValue = aspectRatio ??
    getAspectRatioForHeroBannerImages(deviceType) ?? { x: 0, y: 0 };

  const [loadingState, setLoadingState] = useState<string>('loading');

  return (
    <S.Wrapper
      key={`${aspectValue?.x}:${aspectValue?.y}`}
      $hasBackgroundImage={hasBackgroundImage}
      $heroCard={!!isHeroCard}
      className={className}
      contentAlignment={contentAlignment}
      data-test-id={`HeroBanner-${type}`}
      mediaAnimationEffect={
        mediaAnimationEffect === 'zoomOnHover' ? mediaAnimationEffect : null
      }
      visibleInDesktop={visibleInDesktop}
      visibleInMobile={visibleInMobile}
      {...generateDataTestId('section', `card ${type}`)}
    >
      {!hasBackgroundImage && backgroundColor?.value && (
        <GS.Background $backgroundColor={backgroundColor?.value} />
      )}

      {loadingState === 'error' && (
        <ImagePlaceholder data-test-id='HeroBannerImagePlaceholder' />
      )}
      <>
        <GS.Media
          $backgroundAlignment={backgroundAlignment}
          data-test-id='HeroBannerMedia'
        >
          {useLoader && loadingState === 'loading' && (
            <S.LoaderOverlay data-testid='HeroBannerLoaderOverlay'>
              <Loader data-test-id='DesktopImage' {...loaderProps} />
            </S.LoaderOverlay>
          )}
          <MediaWrapper
            aspectRatio={aspectValue}
            {...(media as ContentfulImageWithFocalPointFragment)}
            isCountDownType={countdown ?? false}
            lazyLoadVideo={lazyLoadVideo}
            mediaWrapperType={mediaWrapperType}
            preload={preload}
            setLoadingState={(state: string) =>
              setLoadingState((prev) => (prev !== 'loaded' ? state : prev))
            }
            sizes={sizes}
            useLoader
          />
        </GS.Media>

        <S.Content $isCountDown={countdown} textAlignment={textAlignment}>
          {countdown && (
            <S.VideoPlayButtonRoot id='video-play-button-root'></S.VideoPlayButtonRoot>
          )}
          <CardContent {...props} />
        </S.Content>
      </>
    </S.Wrapper>
  );
};
